import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from "recharts";
import {
  CATEGORY_NAMES,
  categoryAvg,
  MOCK_EVALUATORS,
  type Candidate,
} from "./mockSelectionData";

interface Props {
  candidates: Candidate[];
  onClose: () => void;
}

const CANDIDATE_COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#8b5cf6"];

// Average categoryAvg across all evaluators
function avgCatScore(candidateId: number, category: string): number {
  const vals = MOCK_EVALUATORS.map((ev) => categoryAvg(candidateId, ev, category));
  return vals.reduce((a, b) => a + b, 0) / vals.length;
}

export function SelectionComparisonModal({ candidates, onClose }: Props) {
  if (candidates.length < 2) return null;

  // Radar data: one entry per category
  const radarData = CATEGORY_NAMES.map((cat) => {
    const entry: Record<string, string | number> = { category: cat };
    candidates.forEach((c) => {
      entry[c.name] = parseFloat(avgCatScore(c.id, cat).toFixed(2));
    });
    return entry;
  });

  // Delta table: sorted by max gap across candidates
  const deltaRows = CATEGORY_NAMES.map((cat) => {
    const scores = candidates.map((c) => ({ name: c.name, score: avgCatScore(c.id, cat) }));
    const vals = scores.map((s) => s.score);
    const maxVal = Math.max(...vals);
    const minVal = Math.min(...vals);
    return { cat, scores, delta: maxVal - minVal, maxVal, minVal };
  }).sort((a, b) => b.delta - a.delta);

  // AI insight: biggest gap
  const biggestGap = deltaRows[0];
  const sortedByScore = [...biggestGap.scores].sort((a, b) => b.score - a.score);
  const topCandidate = sortedByScore[0].name;
  const bottomCandidate = sortedByScore[sortedByScore.length - 1].name;
  const aiText = `${topCandidate} and ${bottomCandidate} diverge most on ${biggestGap.cat} (+${biggestGap.delta.toFixed(1)} gap). Suggested panel discussion: review ${biggestGap.cat} drills from the selection trial.`;

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent
        className="max-w-3xl w-full max-h-[92vh] overflow-y-auto"
        data-testid="comparison-modal"
      >
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="text-lg font-bold text-gray-900">
              Comparing {candidates.length} Candidates
            </DialogTitle>
          </div>
          <div className="flex flex-wrap gap-2 mt-1">
            {candidates.map((c, i) => (
              <span
                key={c.id}
                className="text-xs font-medium px-2 py-0.5 rounded-full text-white"
                style={{ backgroundColor: CANDIDATE_COLORS[i] }}
              >
                {c.name}
              </span>
            ))}
          </div>
        </DialogHeader>

        {/* Radar Chart */}
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
            Category Radar
          </p>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={radarData} margin={{ top: 10, right: 30, bottom: 10, left: 30 }}>
                <PolarGrid stroke="#e5e7eb" />
                <PolarAngleAxis
                  dataKey="category"
                  tick={{ fontSize: 11, fill: "#6b7280" }}
                />
                {candidates.map((c, i) => (
                  <Radar
                    key={c.id}
                    name={c.name}
                    dataKey={c.name}
                    stroke={CANDIDATE_COLORS[i]}
                    fill={CANDIDATE_COLORS[i]}
                    fillOpacity={0.15}
                    strokeWidth={2}
                  />
                ))}
                <Legend />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Grouped Bar Chart */}
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
            Score by Category
          </p>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={radarData} margin={{ top: 0, right: 10, bottom: 0, left: -10 }}>
                <XAxis dataKey="category" tick={{ fontSize: 10, fill: "#9ca3af" }} />
                <YAxis domain={[0, 5]} tick={{ fontSize: 10, fill: "#9ca3af" }} />
                <Tooltip formatter={(v: number) => v.toFixed(2)} />
                <Legend />
                {candidates.map((c, i) => (
                  <Bar key={c.id} dataKey={c.name} fill={CANDIDATE_COLORS[i]} radius={[2, 2, 0, 0]} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Delta Table */}
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
            Score Breakdown (sorted by gap ↓)
          </p>
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-3 py-2 text-left text-xs text-gray-500 font-semibold">Category</th>
                  {candidates.map((c, i) => (
                    <th key={c.id} className="px-3 py-2 text-center text-xs font-semibold" style={{ color: CANDIDATE_COLORS[i] }}>
                      {c.name}
                    </th>
                  ))}
                  <th className="px-3 py-2 text-center text-xs text-gray-400 font-semibold">Gap</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {deltaRows.map(({ cat, scores, delta, maxVal, minVal }) => (
                  <tr key={cat} className="hover:bg-gray-50">
                    <td className="px-3 py-2 text-gray-700 font-medium text-xs">{cat}</td>
                    {scores.map((s) => {
                      const isMax = s.score === maxVal && delta > 0;
                      const isMin = s.score === minVal && delta > 0;
                      return (
                        <td
                          key={s.name}
                          className={`px-3 py-2 text-center text-xs font-semibold ${
                            isMax ? "text-green-700 bg-green-50" : isMin ? "text-red-600 bg-red-50" : "text-gray-700"
                          }`}
                        >
                          {s.score.toFixed(1)}
                        </td>
                      );
                    })}
                    <td className="px-3 py-2 text-center text-xs text-gray-400">{delta.toFixed(1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* AI Insight */}
        <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3" data-testid="ai-insight-box">
          <p className="text-xs font-semibold text-amber-800 mb-1">
            Disagreement Triage Agent
          </p>
          <p className="text-sm text-amber-700 leading-relaxed">{aiText}</p>
        </div>

        {/* Footer */}
        <div className="flex gap-2 pt-1">
          <Button variant="outline" className="flex-1" onClick={() => window.print()}>
            Export Comparison
          </Button>
          <DialogClose asChild>
            <Button className="flex-1 bg-[#1E2761] hover:bg-[#2d3a8c] text-white" data-testid="btn-close-comparison">
              Close
            </Button>
          </DialogClose>
        </div>
      </DialogContent>
    </Dialog>
  );
}
